"use strict";

/**
 * Единая точка входа для ecommerce (window.dataLayer) и будущего контура events.trimiata.ru.
 *
 * Проблема: отложенный push (setTimeout 0) + быстрый переход по ссылке не даёт тег-менеджеру/Метрике
 * гарантированно обработать очередь. Решение: синхронный push в том же тике, затем отложенная навигация
 * через двойной requestAnimationFrame (даёт шанс обработать dataLayer до unload).
 *
 * Service Worker: при наличии активного controller — postMessage с копией данных для офлайн-очереди
 * на стороне SW (регистрация SW — отдельная задача; без SW ветка просто no-op).
 *
 * Сеть на events.trimiata.ru: только сбор черновика (buildTrimiataDraft); HTTP — позже.
 */
const AppAnalytics = class AppAnalytics {

	_isInitialized = false;

	/** @type {boolean} */
	_unloadHookRegistered = false;

	events = {
		trimiataAnalytics: 'TrimiataAnalyticsEvent',
	};

	/**
	 * @param parent
	 * @returns {AppAnalytics}
	 */
	constructor(parent) {
		this.app = parent;
		this.initEvents();
		return this;
	}

	/**
	 * @returns {boolean}
	 */
	isInitialized() {
		return this._isInitialized;
	}

	/**
	 * Регистрация pagehide и флага готовности.
	 */
	initEvents() {
		let component = this;

		if (component._unloadHookRegistered) {
			component._isInitialized = true;
			return;
		}
		component._unloadHookRegistered = true;

		window.addEventListener('pagehide', () => {
			component.onPageHide();
		}, true);

		component._isInitialized = true;
	}

	/**
	 * Зарезервировано: финальный flush очереди в events.trimiata.ru (sendBeacon + keepalive)
	 */
	onPageHide() {
	}

	/**
	 * @param {Object} dataLayerPayload
	 * @param {{navigateTo?: string}|undefined} options
	 */
	raiseEvent(dataLayerPayload, options) {
		let component = this,
			app = component.app;

		if (!dataLayerPayload || typeof dataLayerPayload !== 'object') return;

		let navigateTo = options && options.navigateTo ? String(options.navigateTo) : '';

		if (app.isDevEx() === true || app.isAdmin() === true) {
			if (navigateTo) window.location.assign(navigateTo);
			return;
		}

		component.pushDataLayerSync(dataLayerPayload);

		let trimiataDraft = component.buildTrimiataDraft(dataLayerPayload),
			bundle = {dataLayer: dataLayerPayload, trimiata: trimiataDraft};

		document.dispatchEvent(new CustomEvent(component.events.trimiataAnalytics, {
			bubbles: true,
			detail: bundle,
		}));

		component.notifyServiceWorker(bundle);

		if (navigateTo) component.scheduleNavigationAfterDataLayer(navigateTo);
	}

	/**
	 * @param {Object} payload
	 */
	pushDataLayerSync(payload) {
		this.ensureDataLayer();
		try {
			window.dataLayer.push(payload);
		} catch (e) {
		}
	}

	ensureDataLayer() {
		window.dataLayer = window.dataLayer || [];
	}

	/**
	 * Черновик события под контракт Trimiata (без сетевого вызова).
	 *
	 * @param {Object} dataLayerPayload
	 * @returns {Object}
	 */
	buildTrimiataDraft(dataLayerPayload) {
		let eventName = dataLayerPayload.event || null,
			hint = this.mapEcommerceToContractHint(dataLayerPayload);

		return {
			schema: 'trimiata.analytics.v1',
			capturedAt: new Date().toISOString(),
			eventHint: hint,
			dataLayerEvent: eventName,
			raw: dataLayerPayload,
		};
	}

	/**
	 * Грубое сопоставление Enhanced Ecommerce → типы из TrimiataEventSchema (для следующего этапа).
	 *
	 * @param {Object} dataLayerPayload
	 * @returns {string|null}
	 */
	mapEcommerceToContractHint(dataLayerPayload) {
		let ev = dataLayerPayload.event,
			eco = dataLayerPayload.ecommerce;

		if (!eco && !ev) {
			return null;
		}

		if (ev === 'productClick' && eco && eco.click) {
			return 'product_click';
		}
		if (ev === 'addToCart' && eco && eco.add) {
			return 'add_to_cart';
		}
		if (ev === 'removeFromCart' && eco && eco.remove) {
			return 'remove_from_cart';
		}
		if (ev === 'purchase' && eco && eco.purchase) {
			return 'purchase';
		}
		if (ev === 'detail' && eco && eco.detail) {
			return 'product_view';
		}
		if (ev === 'productImpressions' && eco && eco.impressions) {
			return 'product_impressions_batch';
		}

		return ev || 'ecommerce';
	}

	/**
	 * Опционально: передать копию в Service Worker (IndexedDB/очередь) без доступа к window.dataLayer в SW.
	 *
	 * @param {Object} bundle
	 */
	notifyServiceWorker(bundle) {
		if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
			return;
		}
		try {
			navigator.serviceWorker.controller.postMessage({
				type: 'TRIMIATA_ANALYTICS',
				payload: bundle,
			});
		} catch (e) {
		}
	}

	/**
	 * Навигация после того, как синхронный push уже в очереди dataLayer.
	 *
	 * @param {string} href
	 */
	scheduleNavigationAfterDataLayer(href) {
		if (!href) return;

		queueMicrotask(() => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					window.location.assign(href);
				});
			});
		});
	}
};

export {AppAnalytics};
